import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ImageIcon, ArrowUpRight, X, Maximize2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Motion";
import { ExhibitionPresence } from "@/components/ExhibitionPresence";
import { SEO } from "@/components/SEO";
import { PROJECTS } from "@/lib/projects";

const hasContent = (p) => p.projectName || p.exhibitionName || p.location || p.industry || (p.services && p.services.length) || p.description;

const MetaLine = ({ p, className = "" }) => {
  const bits = [p.exhibitionName, p.location, p.industry].filter(Boolean);
  if (!bits.length) return null;
  return <p className={`text-xs uppercase tracking-[0.2em] text-evo-ash ${className}`}>{bits.join(" · ")}</p>;
};

const ProjectTile = ({ project, index, onOpen }) => {
  const openable = !!project.image;
  return (
    <Reveal delay={(index % 2) * 0.08} className={project.span}>
      <div className="group">
        <button
          type="button"
          data-testid={`project-tile-${project.id}`}
          onClick={() => openable && onOpen(index)}
          data-cursor={openable ? "view" : undefined}
          className="relative block w-full text-left"
        >
          <div className={`relative ${project.ratio} w-full overflow-hidden bg-evo-marble1 border border-white/8 group-hover:border-evo-gold/30 transition-colors duration-500`}>
            {project.image ? (
              <img src={project.image} alt={project.projectName || "EVO exhibition project"} style={{ objectPosition: project.crop || "center" }} className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <ImageIcon size={30} className="text-evo-gold/40 mb-4" strokeWidth={1} />
                <p className="text-xs uppercase tracking-[0.25em] text-evo-ash/50">Project Image</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-evo-ash/30 mt-2">Awaiting approved photo</p>
              </div>
            )}
            <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #C8A97E 0 1px, transparent 1px 14px)" }} />
            {openable && (
              <div className="absolute inset-0 flex items-center justify-center bg-evo-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="inline-flex items-center gap-2 border border-evo-gold text-evo-gold text-[10px] uppercase tracking-[0.25em] px-5 py-3">
                  <Maximize2 size={13} /> View
                </span>
              </div>
            )}
          </div>
        </button>

        {(hasContent(project)) && (
          <div className="mt-5 flex items-start justify-between gap-4 border-t border-white/8 pt-4">
            <div>
              {project.projectName && (
                <p className="font-serif text-2xl italic text-evo-pearl group-hover:text-evo-gold transition-colors">{project.projectName}</p>
              )}
              <MetaLine p={project} className="mt-2" />
            </div>
            <Link to={`/experiences/${project.id}`} data-testid={`project-casestudy-${project.id}`} className="shrink-0 text-evo-ash hover:text-evo-gold transition-colors">
              <ArrowUpRight size={20} />
            </Link>
          </div>
        )}
      </div>
    </Reveal>
  );
};

const Experiences = () => {
  const [active, setActive] = useState(null);
  const project = active !== null ? PROJECTS[active] : null;

  return (
    <div data-testid="experiences-page">
      <SEO title="Portfolio — Selected Exhibition Experiences | EVO Exhibitions" description="Selected exhibition stands delivered by EVO at leading shows including GITEX, ADIPEC and Gulfood in Dubai and the UAE." path="/experiences" />
      <section className="px-6 md:px-12 lg:px-20 pt-40 md:pt-48 pb-16">
        <Reveal><Overline className="block mb-8">Portfolio</Overline></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] text-evo-pearl max-w-4xl">
            Selected Exhibition Experiences
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-10 text-evo-ash font-light max-w-xl leading-relaxed">
            A curated selection of EVO's completed exhibition stands. Click any project to view it full-screen, or open the full case study.
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {PROJECTS.map((p, i) => <ProjectTile key={p.id} project={p} index={i} onOpen={setActive} />)}
        </div>
      </section>

      <ExhibitionPresence />

      <section className="px-6 md:px-12 lg:px-20 py-28 md:py-32">
        <div className="border-t border-white/8 pt-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <h2 className="font-serif font-light text-3xl md:text-5xl text-evo-pearl max-w-xl">
            Have a brief? We'll show you what's possible.
          </h2>
          <Link
            to="/contact"
            data-testid="experiences-cta-btn"
            className="inline-flex items-center gap-3 border border-evo-gold/60 text-evo-gold hover:bg-evo-gold hover:text-evo-black transition-colors duration-300 px-9 py-5 text-xs uppercase tracking-[0.2em] group shrink-0"
          >
            Request a Quotation
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      {/* Full-screen viewer */}
      <AnimatePresence>
        {project && (
          <motion.div
            data-testid="project-lightbox"
            className="fixed inset-0 z-[120] bg-evo-black/95 backdrop-blur-md flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between px-6 md:px-12 h-20 border-b border-white/8 shrink-0">
              <Overline>{project.projectName || "EVO Project"}</Overline>
              <button data-testid="lightbox-close" onClick={() => setActive(null)} className="text-evo-pearl hover:text-evo-gold transition-colors p-2" data-cursor="view">
                <X size={26} />
              </button>
            </div>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
              <motion.div
                className="lg:col-span-8 relative bg-evo-black flex items-center justify-center p-6 md:p-12"
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <img src={project.image} alt={project.projectName || "EVO project"} className="max-w-full max-h-full object-contain" />
              </motion.div>
              <div className="lg:col-span-4 border-l border-white/8 p-8 md:p-12 overflow-y-auto bg-evo-marble1/40">
                {project.projectName && <h3 className="font-serif text-3xl md:text-4xl text-evo-pearl mb-6">{project.projectName}</h3>}
                <MetaLine p={project} className="mb-8" />
                {project.services && project.services.length > 0 && (
                  <div className="mb-8">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-4">Services Delivered</p>
                    <div className="flex flex-wrap gap-2">
                      {project.services.map((s) => (
                        <span key={s} className="text-xs text-evo-pearl border border-white/15 px-3 py-1.5">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {project.description && <p className="text-evo-ash font-light leading-relaxed">{project.description}</p>}
                <Link to={`/experiences/${project.id}`} onClick={() => setActive(null)} className="mt-10 inline-flex items-center gap-2 link-underline text-xs uppercase tracking-[0.2em] text-evo-gold">
                  Full case study <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Experiences;
